import { TriggerController } from 'Components/controllers/trigger-controller';
import 'Components/vm-icon/vm-icon';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { lockScroll, unlockScroll } from 'Utils/dom';
import Styles from './vm-bottom-sheet.scss';

const INITIAL_HEIGHT_RATIO = 0.65;
const MAX_HEIGHT_RATIO = 1;
const DISMISS_THRESHOLD = 0.3;
const VELOCITY_PROJECT_MS = 300; // ms of momentum projected past release to bias the snap target

type DragState = {
    pointerId: number;
    startY: number;
    startHeight: number;
    startTime: number;
};

/**
 * A mobile bottom sheet component that slides up from the bottom of the screen.
 * Supports drag-to-resize/dismiss via the drag handle, backdrop tap to close,
 * and a close button in the header.
 *
 * @slot - Default slot for the sheet body content. Scrolls when its content
 *   exceeds the available height. The body is `position: relative` so
 *   consumers can pin children (e.g. a sticky composer) to its viewport.
 * @slot header - Slot for header content displayed next to the close button.
 * @slot footer - Optional action row below the body (e.g. Save / Discard). Collapses when
 *                nothing is slotted in.
 *
 * @csspart sheet - The sheet panel itself. Style `background-color`, `border-radius`, etc.
 *                  to theme the surface from outside the shadow root.
 * @csspart close-button - The header X close button.
 * @csspart footer - The footer area wrapping the footer slot. Its `box-shadow` is driven by an
 *                   animation where scroll-driven timelines are supported, so overriding
 *                   `box-shadow` from outside needs `!important`.
 *
 * @fires vm-close - Fired on dismissal (backdrop tap, close button, drag dismiss, Escape) AND when the sheet is removed from the DOM while still open.
 *
 * @example
 * Bind to an external trigger via selector — the sheet wires up the click handler itself:
 * ```html
 * <button id="open-info">More info</button>
 *
 * <vm-bottom-sheet trigger="#open-info">
 *   <span slot="header">More info</span>
 *   <p>Sheet body content here</p>
 * </vm-bottom-sheet>
 * ```
 *
 * @example
 * Control visibility manually via the `open` property — useful when the trigger lives
 * in a parent component or the sheet should open in response to non-click events:
 * ```html
 * <vm-bottom-sheet .open=${this.showSheet} @vm-close=${() => (this.showSheet = false)}>
 *   <span slot="header">More info</span>
 *   <p>Sheet body content here</p>
 * </vm-bottom-sheet>
 * ```
 */
@customElement('vm-bottom-sheet')
export class VmBottomSheet extends LitElement {
    static override styles = Styles;

    /** Whether the bottom sheet is visible. */
    @property({ type: Boolean, reflect: true })
    open = false;

    /** Hides the backdrop and disables close-on-outside-click. */
    @property({ type: Boolean, reflect: true, attribute: 'disable-backdrop' })
    disableBackdrop = false;

    /**
     * Keeps the backdrop visible but stops a click on it from dismissing the sheet.
     * Use when the consumer needs an explicit action button as the only dismiss path
     * (e.g. multi-field edit forms with a Save / Discard footer).
     */
    @property({ type: Boolean, reflect: true, attribute: 'disable-backdrop-click' })
    disableBackdropClick = false;

    /**
     * Hides the drag handle and close button, and disables all dismiss gestures
     * (drag, Escape key, backdrop click). Use for sheets that must be dismissed
     * only via explicit action buttons in the content (e.g. the R1 app gate).
     */
    @property({ type: Boolean, reflect: true, attribute: 'non-dismissible' })
    nonDismissible = false;

    /**
     * Skips rendering the header X button (drag-to-close + Escape still work). Use
     * when the consumer provides its own dismiss buttons inside the sheet body and
     * the redundant header X would be visual noise.
     */
    @property({ type: Boolean, reflect: true, attribute: 'hide-close-button' })
    hideCloseButton = false;

    /**
     * Initial height in pixels. Falls back to 65% of the viewport if not set.
     * Reactive — flipping while the sheet is open animates the resize.
     */
    @property({ type: Number, attribute: 'initial-height' })
    initialHeight?: number;

    /** Will open bottom sheet in full height */
    @property({ type: Boolean })
    fullscreen = false;

    /**
     * CSS selector for an external element that opens the sheet on click.
     * The element must exist in the DOM when the sheet connects — binding
     * is one-shot, not observed.
     */
    @property()
    trigger?: string;

    @state()
    private _closing = false;

    @state()
    private _sheetHeight = 0;

    @state()
    private _dragging = false;

    @state()
    private _hasFooter = false;

    @query('.bottom-sheet__sheet')
    private _sheet!: HTMLDivElement;

    @query('.bottom-sheet__body')
    private _bodyEl?: HTMLDivElement;

    /**
     * The internal scrollable body element. Exposed so consumers that need
     * scroll-anchored observers (e.g., an `IntersectionObserver` for infinite
     * scroll) can use it as the observer `root` — the geometry then matches
     * the sheet's own scroll container instead of the viewport.
     */
    get scrollContainer(): HTMLDivElement | null {
        return this._bodyEl ?? null;
    }

    private _dragState: DragState | null = null;

    private _triggerCtrl = new TriggerController(this, {
        getSelector: () => this.trigger,
        onActivate: () => {
            this.open = true;
        },
    });

    override connectedCallback(): void {
        super.connectedCallback();
        this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    override willUpdate(changed: Map<PropertyKey, unknown>): void {
        // Reactive initialHeight flip. Skip mid-drag and mid-close so we don't fight the user or the close animation.
        if (!changed.has('open') && changed.has('initialHeight') && this.open && !this._dragging && !this._closing) {
            this._sheetHeight = this._computeSheetHeight();
        }
    }

    override updated(changed: Map<PropertyKey, unknown>): void {
        if (changed.has('open')) {
            this._triggerCtrl.setExpanded(this.open);
            if (this.open) {
                this._closing = false;
                this._sheetHeight = this._computeSheetHeight();
                this._lockBodyScroll();
                document.addEventListener('keydown', this._handleKeyDown);
                if (this.fullscreen) this._attachViewportListener();
            } else if (changed.get('open') === true) {
                // Only unlock on a real true→false transition — skip the initial
                // undefined→false render so we don't clobber another overlay's lock.
                this._unlockBodyScroll();
                document.removeEventListener('keydown', this._handleKeyDown);
                this._detachViewportListener();
                // Closing destroys the footer slot; reopening builds a new one. An empty
                // slot fires no slotchange, so reset here or the next open shows an empty
                // footer bar left over from this one.
                this._hasFooter = false;
                // Dispatch vm-close from here so it fires on every close path,
                // including programmatic open=false (not just the animated close path).
                this.dispatchEvent(new CustomEvent('vm-close', { bubbles: true, composed: true }));
                // Restore focus to the trigger so keyboard/SR users don't lose their place.
                this._triggerCtrl.focus();
            }
        }
        // React to `fullscreen` toggling mid-open (e.g. a composer inside the
        // sheet grows it to full height on focus). Skip mid-drag/close so we
        // don't fight the user or the exit animation.
        if (
            changed.has('fullscreen') &&
            changed.get('fullscreen') !== undefined &&
            this.open &&
            !this._dragging &&
            !this._closing
        ) {
            this._sheetHeight = this._computeSheetHeight();
            if (this.fullscreen) this._attachViewportListener();
            else this._detachViewportListener();
        }
    }

    /**
     * Height source for the sheet. Fullscreen mode uses `visualViewport.height`
     * so iOS Safari's on-screen keyboard shrinks the sheet — `window.innerHeight`
     * / `100dvh` don't reliably do that on iOS. Non-fullscreen uses the initial
     * ratio of the layout viewport.
     */
    private _computeSheetHeight(): number {
        if (this.fullscreen) {
            return window.visualViewport?.height ?? window.innerHeight;
        }
        // `||` (not `??`) so `initialHeight === 0` falls back to the ratio —
        // consumers passing 0 mean "not set", not "render at zero height".
        return this.initialHeight || window.innerHeight * INITIAL_HEIGHT_RATIO;
    }

    private _onViewportResize = (): void => {
        if (!this.fullscreen || !this.open || this._dragging || this._closing) return;
        this._sheetHeight = this._computeSheetHeight();
    };

    private _attachViewportListener(): void {
        window.visualViewport?.addEventListener('resize', this._onViewportResize);
    }

    private _detachViewportListener(): void {
        window.visualViewport?.removeEventListener('resize', this._onViewportResize);
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
        // Only unlock if the sheet was actually open — otherwise we'd clobber
        // the scroll-lock state of any concurrently-open overlay.
        if (this.open) {
            this._unlockBodyScroll();
            // Fire vm-close so consumers caching open-state don't strand
            // `true` and reopen the sheet on reattach.
            this.dispatchEvent(new CustomEvent('vm-close', { bubbles: true, composed: true }));
        }
        document.removeEventListener('keydown', this._handleKeyDown);
        this._detachViewportListener();
        this._detachDragListeners();
        this._dragState = null;
        this._dragging = false;
    }

    private _lockBodyScroll(): void {
        lockScroll();
    }

    private _unlockBodyScroll(): void {
        unlockScroll();
    }

    private _handleKeyDown(e: KeyboardEvent): void {
        if (this.nonDismissible) return;
        if (e.key === 'Escape') {
            this._animateClose();
        }
    }

    private _animateClose(): void {
        this._closing = true;
    }

    private _onFooterSlotChange = (e: Event): void => {
        // A wrapper component can assign its own <slot> here to forward its light DOM on.
        // `flatten: true` looks through that to the real content, so an empty forwarding
        // slot doesn't count as a footer.
        this._hasFooter = (e.target as HTMLSlotElement).assignedElements({ flatten: true }).length > 0;
    };

    private _onAnimationEnd(e: AnimationEvent): void {
        // The footer's scroll shadow animates too, and animationend bubbles — only the
        // sheet's own close animation should end the close.
        if (e.target !== e.currentTarget) return;
        if (this._closing) {
            // Setting open=false triggers updated(), which handles vm-close, focus,
            // and scroll-unlock — single source of truth for the close lifecycle.
            this.open = false;
        }
    }

    private _onBackdropClick(): void {
        if (this.disableBackdrop || this.disableBackdropClick || this.nonDismissible) return;
        this._animateClose();
    }

    private _onSheetClick(e: Event): void {
        e.stopPropagation();
    }

    private _onPointerDown(e: PointerEvent): void {
        if (!e.isPrimary || e.button !== 0 || this._dragState) {
            return;
        }

        const sheet = this._sheet;
        if (!sheet) {
            return;
        }

        this._dragState = {
            pointerId: e.pointerId,
            startY: e.clientY,
            startHeight: sheet.getBoundingClientRect().height,
            startTime: Date.now(),
        };

        this._dragging = true;

        // Track move/up on window rather than capturing the pointer on the header: a
        // mouse drag keeps tracking once the cursor leaves the header, and clicks on
        // buttons inside it (close, slotted back buttons) aren't retargeted to the
        // capture element.
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerCancel);
    }

    private _detachDragListeners(): void {
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerCancel);
    }

    private _onPointerMove = (e: PointerEvent): void => {
        const state = this._dragState;
        const sheet = this._sheet;
        if (!state || !sheet || e.pointerId !== state.pointerId) {
            return;
        }

        const deltaY = e.clientY - state.startY;
        // Fullscreen sheets can only be dragged DOWNWARD (toward dismiss) — an
        // upward drag would pull the sheet taller than its committed max, which
        // then springs back on release and looks like a bug.
        const clampedDelta = this.fullscreen ? Math.max(0, deltaY) : deltaY;
        const newHeight = Math.min(
            window.innerHeight * MAX_HEIGHT_RATIO,
            Math.max(100, state.startHeight - clampedDelta),
        );
        this._sheetHeight = newHeight;
    };

    private _endDrag(e: PointerEvent): DragState | null {
        const state = this._dragState;
        if (!state || e.pointerId !== state.pointerId) {
            return null;
        }

        this._detachDragListeners();
        this._dragState = null;
        this._dragging = false;
        return state;
    }

    private _onPointerCancel = (e: PointerEvent): void => {
        const state = this._endDrag(e);
        if (state) {
            this._sheetHeight = state.startHeight;
        }
    };

    private _onPointerUp = (e: PointerEvent): void => {
        const state = this._endDrag(e);
        if (!state) {
            return;
        }

        const sheet = this._sheet;
        if (!sheet) {
            return;
        }

        // In fullscreen we clamp deltaY the same way `_onPointerMove` does — an
        // upward drag never grows the sheet past its committed max, and the
        // release-path math (`currentHeight` / `projectedHeight`) has to see
        // the same clamped value or it'll snap back to `window.innerHeight`
        // instead of the keyboard-aware `_computeSheetHeight()` value.
        const rawDelta = e.clientY - state.startY;
        const deltaY = this.fullscreen ? Math.max(0, rawDelta) : rawDelta;
        const elapsed = Date.now() - state.startTime;
        const velocity = elapsed > 0 ? deltaY / elapsed : 0; // px/ms, positive = dragged down
        // Fullscreen sheets only ever rest at full height — pin both snap
        // targets to the current computed height so drag-down only chooses
        // between "stay full" and "dismiss". `_computeSheetHeight()` returns
        // `visualViewport.height` in fullscreen, which iOS Safari shrinks with
        // the on-screen keyboard — using `window.innerHeight` here would put
        // the dismiss threshold above `startHeight`, dismissing on a plain tap
        // whenever the keyboard is up.
        const maxHeight = this.fullscreen ? this._computeSheetHeight() : window.innerHeight * MAX_HEIGHT_RATIO;
        // Delegate to `_computeSheetHeight()` for the non-fullscreen initial
        // target — keeps the "treat `initialHeight === 0` as unset" rule in
        // one place instead of duplicating it here as `?? …` (which would
        // pin the snap target to 0 and dismiss on any drag).
        const initialHeight = this.fullscreen ? maxHeight : this._computeSheetHeight();
        const currentHeight = state.startHeight - deltaY;
        // Project where the height would settle by carrying the release velocity forward;
        // a fast downward flick lands lower (closer to dismiss / initial), a fast upward flick lands higher.
        const projectedHeight = currentHeight - velocity * VELOCITY_PROJECT_MS;

        if (projectedHeight < initialHeight * (1 - DISMISS_THRESHOLD)) {
            // Dismiss via drag — animate height to 0, then fire close
            this._sheetHeight = 0;
            const handler = () => {
                // updated() will dispatch vm-close, restore focus, and unlock scroll.
                this.open = false;
                sheet.removeEventListener('transitionend', handler);
            };
            sheet.addEventListener('transitionend', handler);
        } else {
            // Snap to whichever of the two allowed heights is closer to where the drag would settle
            const distToInitial = Math.abs(projectedHeight - initialHeight);
            const distToMax = Math.abs(projectedHeight - maxHeight);
            this._sheetHeight = distToInitial <= distToMax ? initialHeight : maxHeight;
        }
    };

    override render() {
        if (!this.open) return nothing;

        return html`
            <div
                class="bottom-sheet__overlay ${this._closing ? 'bottom-sheet__overlay--closing' : ''}"
                @click=${this._onBackdropClick}
            >
                ${this.disableBackdrop ? nothing : html`<div class="bottom-sheet__backdrop"></div>`}
                <div
                    part="sheet"
                    class="bottom-sheet__sheet ${this._closing ? 'bottom-sheet__sheet--closing' : ''}"
                    role="dialog"
                    aria-modal="${!this.disableBackdrop}"
                    aria-labelledby="vm-bottom-sheet-title"
                    style="${this.nonDismissible ? '' : `height: ${this._sheetHeight}px;`} ${this._dragging
                        ? 'transition: none;'
                        : ''}"
                    @click=${this._onSheetClick}
                    @animationend=${this._onAnimationEnd}
                >
                    ${this.nonDismissible
                        ? html`<span id="vm-bottom-sheet-title" class="bottom-sheet__visually-hidden"
                              ><slot name="header"></slot
                          ></span>`
                        : html`
                              <!-- Header (draggable) -->
                              <div class="bottom-sheet__header" @pointerdown=${this._onPointerDown}>
                                  <div class="bottom-sheet__drag-handle"></div>
                                  <div class="bottom-sheet__header-row">
                                      <div id="vm-bottom-sheet-title" class="bottom-sheet__header-title">
                                          <slot name="header"></slot>
                                      </div>
                                      ${this.hideCloseButton
                                          ? nothing
                                          : html`
                                                <button
                                                    part="close-button"
                                                    class="bottom-sheet__close-btn"
                                                    @click=${this._animateClose}
                                                    aria-label="Close"
                                                >
                                                    <vm-icon name="x-close" size="md"></vm-icon>
                                                </button>
                                            `}
                                  </div>
                              </div>
                          `}

                    <!-- Scrollable body -->
                    <div class="bottom-sheet__body">
                        <slot></slot>
                    </div>

                    <!-- Footer (in flow, below the body) -->
                    <div
                        part="footer"
                        class="bottom-sheet__footer ${this._hasFooter ? '' : 'bottom-sheet__footer--empty'}"
                    >
                        <slot name="footer" @slotchange=${this._onFooterSlotChange}></slot>
                    </div>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-bottom-sheet': VmBottomSheet;
    }
    interface HTMLElementEventMap {
        'vm-close': CustomEvent;
    }
}
